import React from 'react';
import { DownOutlined } from '@ant-design/icons';
import { Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { STEP_STATUS } from '../constants/searchSteps';

/**
 * Header component for individual search steps
 * @param {Object} props - Component props
 * @param {Object} props.step - Step configuration object
 * @param {string} props.status - Current step status
 * @param {boolean} props.isActive - Whether step is currently active
 * @param {boolean} props.canClick - Whether step header can be clicked
 * @param {Array} props.selections - Current step selections (array of objects with value/label)
 * @param {Function} props.onStepClick - Step click handler
 * @returns {JSX.Element} StepHeader component
 */
const StepHeader = ({
  step,
  status,
  isActive,
  canClick,
  selections,
  onStepClick,
}) => {
  const { t } = useTranslation('treatment-search');

  const isCompleted = status === STEP_STATUS.COMPLETED;
  const isDisabled = status === STEP_STATUS.DISABLED;

  const selectionsArray = Array.isArray(selections)
    ? selections
    : selections
      ? [selections]
      : [];
  const hasSelections = selectionsArray.length > 0;

  const getHeaderStyles = () => {
    if (isActive)
      return 'bg-[#E9F1D9] border border-[#7CB305] rounded-t-[16px]';
    if (isCompleted) return 'bg-[#E9F1D9] rounded-[16px]';
    if (isDisabled) return 'bg-[#FAFAFA] rounded-[16px]';
    return 'bg-gray-100 hover:bg-gray-150 rounded-[16px]';
  };

  const getBadgeStyles = () => {
    if (isCompleted || isActive) return 'bg-[#105235] text-white';
    if (isDisabled) return 'bg-[#D9D9D9] text-gray-600';
    return 'bg-gray-300 text-gray-600';
  };

  const getTextStyles = () => {
    if (isActive || isCompleted) return 'text-black';
    if (isDisabled) return 'text-gray-500';
    return 'text-gray-600';
  };

  /**
   * Gets the display label for a selection based on step configuration
   * @param {*} selection - The selection object with label/value or string
   * @returns {string} Display label
   */
  const getSelectionLabel = (selection) => {
    if (typeof selection === 'string') return selection;
    if (typeof selection === 'number') return String(selection);
    if (typeof selection === 'object' && selection) {
      const label =
        selection.label ||
        selection.title ||
        selection.name ||
        selection.french_name ||
        selection.english_name ||
        selection.display_name ||
        selection.text ||
        selection.displayLabel;
      if (label) return String(label);

      // If no label found, try value field
      if (selection.value) return String(selection.value);
      if (selection.key) return String(selection.key);
      if (selection.id) return String(selection.id);
    }
    return String(selection || '');
  };

  /**
   * Truncates text if it's too long for tag display
   * @param {string} text - Text to truncate
   * @param {number} maxLength - Maximum length before truncation
   * @returns {string} Truncated text
   */
  const truncateTagText = (text, maxLength = 50) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div
      className={`flex items-center justify-between p-3 sm:p-4 transition-all duration-300 ${
        canClick ? 'cursor-pointer' : 'cursor-not-allowed'
      } ${getHeaderStyles()}`}
      onClick={() => onStepClick(step.key)}
    >
      <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
        <div
          className={`flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 rounded-full font-semibold text-xs sm:text-sm flex-shrink-0 ${getBadgeStyles()}`}
        >
          {step.key}
        </div>

        <span className={`font-medium text-sm sm:text-base truncate ${getTextStyles()}`}>
          {t(step.title)}
        </span>
      </div>

      <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0 ml-2">
        {hasSelections && (
          <div className="hidden sm:flex items-center gap-1 max-w-xs lg:max-w-2xl overflow-hidden">
            {selectionsArray.slice(0, 2).map((selection, index) => (
              <Tag
                key={index}
                color="#5B8C00"
                className="flex-shrink-0 max-w-[150px] lg:max-w-md truncate text-xs"
                title={getSelectionLabel(selection)}
              >
                {truncateTagText(getSelectionLabel(selection), 30)}
              </Tag>
            ))}
            {selectionsArray.length > 2 && (
              <Tag color="#5B8C00" className="flex-shrink-0 text-xs">
                +{selectionsArray.length - 2}
              </Tag>
            )}
          </div>
        )}
        {/* Mobile: Show count badge only */}
        {hasSelections && (
          <div className="flex sm:hidden">
            <Tag color="#5B8C00" className="flex-shrink-0 text-xs">
              {selectionsArray.length}
            </Tag>
          </div>
        )}
        {!isActive && <DownOutlined className="text-black text-xs sm:text-sm" />}
      </div>
    </div>
  );
};

export default StepHeader;
